import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  CommandDashboardKeys,
  CommandDashboardTaskIds,
  CommandDashboardTaskStatuses,
  getLoginActivityTileMetrics,
  refreshLoginActivityTileMetrics,
} from '../../api/command-dashboard-api';
import { handleErrors } from '../../utils/errors';
import { Messages } from '../../utils/message-constants';
import { useStore } from '../state/zustand-store';
import {
  isCommandDashboardRefreshAlreadyRunning,
  useCommandDashboardTileTaskStatus,
} from './use-command-dashboard-tile-task-status';
import { useTrackCommandDashboardTileRefresh } from './use-tile-refresh';

export function useLoginActivityTileMetrics(enabled = true) {
  const trackRefresh = useTrackCommandDashboardTileRefresh();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  // Whether a refresh is running lives in the backend's TaskStatus rows, not in this hook - so it
  // survives a reload, and is already correct for a refresh someone else (or the nightly
  // dispatch) started. Fetched here rather than page-level so it is only requested when a
  // task-backed tile is actually on the grid.
  const { statusQuery, isRefreshing } = useCommandDashboardTileTaskStatus(
    CommandDashboardTaskIds.LoginActivity,
    CommandDashboardKeys.LoginActivityTile,
    enabled
  );

  const query = useQuery({
    queryKey: [CommandDashboardKeys.LoginActivityTile],
    queryFn: getLoginActivityTileMetrics,
    enabled,
    placeholderData: keepPreviousData,
    meta: {
      errorMessage: Messages.CommandDashboard.LoginActivityTileLoadError,
    },
  });

  const derived = useMemo(() => {
    const dailyCounts = query.data?.dailyCounts ?? [];
    const totalLogins7d = dailyCounts.reduce(
      (sum, day) => sum + day.totalLogins,
      0
    );
    const leakedPassword7d = dailyCounts.reduce(
      (sum, day) => sum + day.leakedPassword,
      0
    );
    const suspiciousBlocked7d = dailyCounts.reduce(
      (sum, day) => sum + day.suspiciousBlocked,
      0
    );
    const genericDisable7d = dailyCounts.reduce(
      (sum, day) => sum + day.genericDisable,
      0
    );
    // dailyCounts' chronologically-last bucket is NOT reliably today's - right after the nightly
    // run (before any manual refresh that day) it's still yesterday's, since the backend's
    // "today" has zero elapsed time at midnight (see CommandDashboardLoginActivityTask.
    // lastBucketDate). Matching against the backend-stamped `today` (domain-timezone, not the
    // browser's) is what actually tells today's bucket apart from a stale one.
    const todayBucket = dailyCounts.find(
      (day) => day.date === query.data?.today
    );
    const failedLoginsToday = todayBucket?.failedLogin ?? 0;

    return {
      totalLogins7d,
      leakedPassword7d,
      suspiciousBlocked7d,
      genericDisable7d,
      failedLoginsToday,
    };
  }, [query.data?.dailyCounts, query.data?.today]);

  const onRefresh = async () => {
    const previousStatuses =
      queryClient.getQueryData<CommandDashboardTaskStatuses>([
        CommandDashboardKeys.TaskStatuses,
      ]);
    queryClient.setQueryData<CommandDashboardTaskStatuses>(
      [CommandDashboardKeys.TaskStatuses],
      (old = {}) => ({
        ...old,
        [CommandDashboardTaskIds.LoginActivity]: 'RUNNING',
      })
    );
    try {
      await refreshLoginActivityTileMetrics();
      trackRefresh();
      setSnackbar({
        open: true,
        severity: 'info',
        message: Messages.CommandDashboard.LoginActivityTileRefreshStarted,
      });
    } catch (error) {
      if (isCommandDashboardRefreshAlreadyRunning(error)) {
        // The conflict means a run is in flight - keep the optimistic RUNNING row so Refresh
        // stays disabled, and wait for that task's completion the same way as a kick-off we
        // started ourselves.
        queryClient.setQueryData<CommandDashboardTaskStatuses>(
          [CommandDashboardKeys.TaskStatuses],
          (old = {}) => ({
            ...old,
            [CommandDashboardTaskIds.LoginActivity]: 'RUNNING',
          })
        );
        setSnackbar({
          open: true,
          severity: 'info',
          message:
            Messages.CommandDashboard.LoginActivityTileRefreshAlreadyRunning,
        });
        return;
      }
      queryClient.setQueryData<CommandDashboardTaskStatuses>(
        [CommandDashboardKeys.TaskStatuses],
        (current = {}) => {
          const restored = { ...current };
          const previousStatus =
            previousStatuses?.[CommandDashboardTaskIds.LoginActivity];
          if (previousStatus) {
            restored[CommandDashboardTaskIds.LoginActivity] = previousStatus;
          } else {
            delete restored[CommandDashboardTaskIds.LoginActivity];
          }
          return restored;
        }
      );
      handleErrors({
        error,
        snackbarMessage:
          Messages.CommandDashboard.LoginActivityTileRefreshError,
      });
    }
  };

  return {
    snapshot: query.data,
    ...derived,
    lastUpdated: query.data?.lastUpdated ?? null,
    truncated: query.data?.truncated ?? false,
    // The status map is part of this tile's initial load, so the Refresh control is never drawn
    // from a status that has not arrived yet.
    isLoading: query.isLoading || statusQuery.isLoading,
    isFetching: query.isFetching || statusQuery.isLoading,
    isRefreshing,
    onRefresh,
  };
}
